//
// # Dot Map & Dot Inference
//

// Local Imports
import { Point, schdata } from "GenAlphaModel";
import { Dot } from "./dot";
import type { SchematicRender } from "./render";
import type { WireSegment } from "./wire";
import { buildConnectivity } from "../../routing";

// Infer Dot locations from wire geometry in a schematic
function inferDots(sch: SchematicRender): Set<Dot> {
  // Zero-length segments are excluded: they are invisible, and counting them
  // turns an ordinary corner into a spurious junction. A drag which collapses
  // a wire onto a coincident pin deliberately leaves one behind as the
  // abutment marker holding that net together (nets are built from wire
  // segments, so deleting it would disconnect the pins) — it must not draw.
  const wires = sch.wires
    .values()
    .filter((w) => !Point.eq(w.data.endpoints[0], w.data.endpoints[1]));

  // Wires only. A pin resting on a wire's span is not a connection — the
  // extractor requires a wire *endpoint* inside the pin's box — so drawing a
  // dot there would assert a connection the netlist does not have.
  const conn = buildConnectivity(wires);

  const dots: Set<Dot> = new Set();
  for (const pt of conn.candidatePoints()) {
    if (conn.isDot(pt)) dots.add(Dot.fromCenter(pt, sch));
  }
  return dots;
}

// # Preview anchors
//
// Dots are re-inferred only when an edit commits, so while a move mode is
// previewing geometry every dot on screen is a frame behind. Hiding all of them
// for the duration is correct but wildly over-broad: a drag in one corner blanks
// every dot in the schematic.
//
// Instead, capture what each dot *rests on* when the preview starts — the wires
// whose geometry passes through its center — and hide only the dots whose
// supporting wires have since moved. Dots away from the drag are untouched, so
// they keep drawing.
//
// The capture is a single pass at preview start; the per-frame test is then a
// handful of point comparisons per dot, rather than the whole-schematic
// connectivity rebuild `inferDots` performs.
export interface DotAnchor {
  dot: Dot;
  // The wires supporting this dot, with their endpoints as of preview start.
  // A wire whose endpoints still match has not moved, so it still supports the
  // dot exactly as it did when the dot was inferred.
  supports: Array<{ wire: WireSegment; start: Point; end: Point }>;
}

// True if `wire`'s current geometry passes through `pt` — its endpoints, and
// any point along its Manhattan span. Zero tolerance: this asks whether the dot
// was inferred from this wire, not whether a cursor is near it. Mirrors the
// exactness `inferDots` gets from `buildConnectivity`.
function wireTouches(wire: WireSegment, pt: Point): boolean {
  const [a, b] = wire.data.endpoints;
  // Zero-length segments are excluded from inference, so they support nothing.
  if (Point.eq(a, b)) return false;
  const segments = schdata.Manhattan.calc([...wire.data.endpoints]);
  if (!segments) return false;
  return segments.some((seg) => schdata.Manhattan.hitTest(seg, pt, 0));
}

// Snapshot every dot's supporting wires. Called once when a live-preview move
// begins; O(dots x wires), which is the same order as a single dot inference and
// happens once per drag rather than once per frame.
function captureDotAnchors(sch: SchematicRender): Array<DotAnchor> {
  const wires = Array.from(sch.wires.all());
  return Array.from(sch.dots).map((dot) => ({
    dot,
    supports: wires
      .filter((wire) => wireTouches(wire, dot.center))
      .map((wire) => ({
        wire,
        // Copied, not aliased: the preview mutates the wire's own endpoints in
        // place, so holding the live objects would compare them against
        // themselves and never report a move.
        start: Point.new(wire.data.endpoints[0].x, wire.data.endpoints[0].y),
        end: Point.new(wire.data.endpoints[1].x, wire.data.endpoints[1].y),
      })),
  }));
}

// The dots whose supporting geometry has moved since the anchors were captured,
// and which are therefore drawn in a position the schematic no longer has.
//
// A dot with no recorded supports is never hidden: that means the capture found
// nothing under it, so we have no evidence it is stale and the safe answer is to
// keep drawing what the last commit inferred.
function staleDots(anchors: ReadonlyArray<DotAnchor>): Set<Dot> {
  const stale = new Set<Dot>();
  for (const anchor of anchors) {
    const moved = anchor.supports.some(
      ({ wire, start, end }) =>
        !Point.eq(wire.data.endpoints[0], start) ||
        !Point.eq(wire.data.endpoints[1], end)
    );
    if (moved) stale.add(anchor.dot);
  }
  return stale;
}

export const Dots = {
  infer: inferDots,
  captureAnchors: captureDotAnchors,
  stale: staleDots,
};
